import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { COUNTIES, MARKET_TABS, PROPERTY_TYPES, STRATEGIES, TIERS, SOURCES, CALL_STATUSES } from '@/lib/buyersV2';

const ANY = '__any__';
const cls = 'bg-white border-gray-300 h-9 text-sm';

function Pick({ label, k, options, f, set }) {
  return (
    <div className="space-y-1"><Label className="text-[11px] text-gray-500">{label}</Label>
      <Select value={f[k] || ANY} onValueChange={v => set(k, v === ANY ? '' : v)}>
        <SelectTrigger data-testid={`filter-${k}`} className={cls}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white max-h-64"><SelectItem value={ANY}>Any</SelectItem>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Txt({ label, k, f, set, type = 'text', placeholder }) {
  return (
    <div className="space-y-1"><Label className="text-[11px] text-gray-500">{label}</Label>
      <Input data-testid={`filter-${k}`} type={type} value={f[k] || ''} placeholder={placeholder} onChange={e => set(k, e.target.value)} className={cls} />
    </div>
  );
}

function Flag({ label, k, f, set }) {
  return (
    <label className="flex items-center justify-between text-sm text-gray-700 py-1">
      {label}<Switch data-testid={`filter-${k}`} checked={f[k] === 'true'} onCheckedChange={v => set(k, v ? 'true' : '')} />
    </label>
  );
}

export function FilterPanel({ open, onOpenChange, filters, onApply, tags }) {
  const [f, setF] = useState(filters);
  useEffect(() => { if (open) setF(filters); }, [open, filters]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const p = { f, set };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="filter-panel">
        <SheetHeader><SheetTitle>Filter Buyers</SheetTitle></SheetHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-3">
            <Txt label="Cities (comma separated)" k="city" placeholder="Miami, Homestead" {...p} />
            <Txt label="Zips (comma separated)" k="zip" placeholder="33157, 33176" {...p} />
            <Txt label="Bought in that area at least N times" k="min_count_in_zip" type="number" {...p} />
            <Txt label="Last purchase within (days)" k="last_purchase_within_days" type="number" {...p} />
            <Txt label="Price min" k="price_min" type="number" {...p} />
            <Txt label="Price max" k="price_max" type="number" {...p} />
            <Txt label="Beds min" k="beds_min" type="number" {...p} />
            <Txt label="Baths min" k="baths_min" type="number" {...p} />
            <Pick label="County" k="county" options={COUNTIES} {...p} />
            <Pick label="Market" k="market_tab" options={MARKET_TABS} {...p} />
            <Pick label="Tier" k="tier" options={TIERS} {...p} />
            <Pick label="Strategy" k="strategy" options={STRATEGIES} {...p} />
            <Pick label="Property type" k="property_type" options={PROPERTY_TYPES} {...p} />
            <Pick label="Call status" k="call_status" options={CALL_STATUSES} {...p} />
            <Pick label="Source" k="source" options={SOURCES} {...p} />
            <Pick label="Tag" k="tags" options={tags} {...p} />
          </div>
          <div className="border-t border-gray-100 pt-3">
            <Flag label="Zip preference only" k="zip_preference" {...p} />
            <Flag label="Has mobile phone" k="has_mobile" {...p} />
            <Flag label="Has a non-DNC phone" k="not_dnc" {...p} />
            <Flag label="SMS opt-in" k="sms_opt_in" {...p} />
          </div>
        </div>
        <div className="flex gap-2 sticky bottom-0 bg-white pt-2">
          <Button variant="outline" className="flex-1" data-testid="filter-clear" onClick={() => setF({ q: f.q || '' })}>Clear</Button>
          <Button className="flex-1 bg-[#0073ea] hover:bg-[#0060c2] text-white" data-testid="filter-apply" onClick={() => { onApply(f); onOpenChange(false); }}>Apply</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
